import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useTenantSettings } from '@/features/auth/hooks';
import { useAuth } from "@/features/auth";

interface ThemeContextType {
  primaryColor: string;
  secondaryColor: string;
  updateColors: (primary: string, secondary: string) => void;
  isLoading: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: ReactNode }) {
  // Tenta usar o contexto de autenticação, mas não falha se não estiver disponível
  let authContext;
  try {
    authContext = useAuth();
  } catch (error) {
    // Se o contexto de autenticação não estiver disponível, usa valores padrão
    authContext = { profile: null };
  }

  // Tenta usar o hook de configurações do tenant, mas não falha se não estiver disponível
  let tenantSettings;
  try {
    tenantSettings = useTenantSettings();
  } catch (error) {
    // Se o hook não estiver disponível, usa valores padrão
    tenantSettings = { settings: null, isLoading: false };
  }

  const { profile } = authContext;
  const { settings, isLoading } = tenantSettings;
  
  const [primaryColor, setPrimaryColor] = useState<string>('#3b82f6'); // Azul padrão
  const [secondaryColor, setSecondaryColor] = useState<string>('#f59e0b'); // Laranja padrão

  // Atualiza as cores quando as configurações são carregadas
  useEffect(() => {
    if (settings) {
      const newPrimaryColor = settings.cor_primaria || '#3b82f6';
      const newSecondaryColor = settings.cor_secundaria || '#f59e0b';
      
      setPrimaryColor(newPrimaryColor);
      setSecondaryColor(newSecondaryColor);
      
      // Aplica as cores às variáveis CSS
      applyColorsToDOM(newPrimaryColor, newSecondaryColor);
    }
  }, [settings]);

  // Função para aplicar as cores às variáveis CSS
  const applyColorsToDOM = (primary: string, secondary: string) => {
    // Define as variáveis CSS no root para que possam ser usadas em toda a aplicação
    const root = document.documentElement;
    
    // Cores principais
    root.style.setProperty('--primary-color', primary);
    root.style.setProperty('--secondary-color', secondary);
    
    // Variantes mais claras para hover, foco, etc.
    root.style.setProperty('--primary-light', lightenColor(primary, 10));
    root.style.setProperty('--secondary-light', lightenColor(secondary, 10));
    
    // Variantes mais escuras
    root.style.setProperty('--primary-dark', darkenColor(primary, 10));
    root.style.setProperty('--secondary-dark', darkenColor(secondary, 10));
  };

  // Função para atualizar as cores
  const updateColors = (primary: string, secondary: string) => {
    setPrimaryColor(primary);
    setSecondaryColor(secondary);
    applyColorsToDOM(primary, secondary);
  };

  // Funções auxiliares para ajustar cores
  function lightenColor(color: string, percent: number): string {
    return adjustColor(color, percent);
  }
  
  function darkenColor(color: string, percent: number): string {
    return adjustColor(color, -percent);
  }
  
  function adjustColor(color: string, percent: number): string {
    // Remove o # se existir
    let hex = color.startsWith('#') ? color.slice(1) : color;
    
    // Converte para RGB
    let r = parseInt(hex.substring(0, 2), 16);
    let g = parseInt(hex.substring(2, 4), 16);
    let b = parseInt(hex.substring(4, 6), 16);
    
    // Ajusta os valores RGB
    r = Math.min(255, Math.max(0, r + Math.round(r * (percent / 100))));
    g = Math.min(255, Math.max(0, g + Math.round(g * (percent / 100))));
    b = Math.min(255, Math.max(0, b + Math.round(b * (percent / 100))));
    
    // Converte de volta para hex
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  }

  return (
    <ThemeContext.Provider
      value={{
        primaryColor,
        secondaryColor,
        updateColors,
        isLoading
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}; 